import React, {useState} from 'react';
import { Tag } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {useLocation} from "wouter";
import axios from "@common/axios";
import Button from "@common/Button";
import {
    ArrowForwardRounded,
    PersonAddRounded,
    SettingsRounded
} from "@mui/icons-material";
import Tooltip from "@common/Tooltip";
import ManageUsersDropdown from "./ManageUsersDropdown";
import TableAntD from "@common/TableAntD";

const ClassesTable = ({
                          view = 'teacher',
                          searchQuery = '',
                          teacherClasses = [],
                          handleOpenDrawer,
                          handleRefreshClasses,
                          addUserTooltipText = 'Add user',
                          refetchTrigger = 0
                      }) => {
    const [, navigate] = useLocation();
    const [allClasses, setAllClasses] = useState([]);

    const fetchData = React.useCallback(async (pagingParams, handleTableData, handlePagingData, abortController) => {
        try {
            // Fetch all classes data
            const endpoint = view === 'teacher'
                ? '/web/ui/myClasses/viewMyClasses'
                : '/web/ui/myClasses/viewSchoolClasses';

            const response = await axios.get(endpoint, {
                signal: abortController.signal
            });

            let classes = response.data || [];

            setAllClasses(classes);

            if (searchQuery.trim()) {
                classes = classes.filter(c =>
                    c.className?.toLowerCase().includes(searchQuery.toLowerCase())
                );
            }

            const totalResults = classes.length;
            const startIndex = (pagingParams.pageNumber - 1) * pagingParams.pageSize;
            const endIndex = startIndex + pagingParams.pageSize;
            const paginatedClasses = classes.slice(startIndex, endIndex);

            handleTableData(paginatedClasses);
            handlePagingData({
                pageSize: pagingParams.pageSize,
                totalResults: totalResults,
            });
        } catch (error) {
            if (error.name !== 'CanceledError') {
                console.error('Error fetching classes:', error);
                handleTableData([]);
                handlePagingData({
                    pageSize: pagingParams.pageSize,
                    totalResults: 0
                });
            }
        }
    }, [view, searchQuery]);

    const columns = [
        {
            title: 'Class Name',
            dataIndex: 'className',
            key: 'className',
            render: (text, record) => {
                // Check if this class is not in teacherClasses
                const isNotTeacherClass = !teacherClasses.some(teacherClass => teacherClass.classId === record.classId);

                return (
                    <div>
                        <Button icon={<ArrowForwardRounded />} iconPosition={'right'} type={'text'} onClick={() => navigate(`/web/ui/view/classes/schoolClasses/class/${record.classId}`)}>
                            {text}
                        </Button>
                        {isNotTeacherClass && (
                            <div style={{ marginTop: '1px' }}>
                                <Tag color={'geekblue'} icon={<InfoCircleOutlined />}>
                                    Not your class
                                </Tag>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Class Key',
            dataIndex: 'classCode',
            key: 'classCode',
        },
        {
            title: 'Total Students',
            dataIndex: 'totalStudents',
            key: 'totalStudents'
        },
        {
            title: 'Total Teachers',
            dataIndex: 'totalTeachers',
            key: 'totalTeachers'
        },
        {
            title: 'School Year',
            key: 'schoolYearDescription',
            render: (_, record) => {
                return record.schoolYearDescription || 'No year assigned';
            }
        },
        {
            title: 'Calendar Year',
            dataIndex: 'calendarYear',
            key: 'calendarYear'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                const isUsersClass = teacherClasses.some(tc => tc.classId === record.classId);

                return (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Tooltip title={'Class Settings'}>
                            <Button
                                type={'text'}
                                onClick={() => handleOpenDrawer(record.classId, 'EditClassDrawer')}
                                aria-label={'Class settings'}
                            >
                                <SettingsRounded />
                            </Button>
                        </Tooltip>

                        <ManageUsersDropdown
                            classDetails={record}
                            handleAddStudent={() => handleOpenDrawer(record.classId, 'ManageClassUsers')}
                            handleRefreshClasses={handleRefreshClasses}
                            addUserTooltipText={addUserTooltipText}
                            usersClass={isUsersClass}
                            placement={'topRight'}
                            renderTrigger={
                                <Tooltip title={addUserTooltipText}>
                                    <Button type={'text'} aria-label={addUserTooltipText} data-gtm-value="classes-add-user">
                                        <PersonAddRounded />
                                    </Button>
                                </Tooltip>
                            }
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <div>
            <TableAntD
                dynamicWidth={true}
                columnData={columns}
                pagingDependencies={[refetchTrigger, view, searchQuery]}
                pagingData={{
                    fetchFunction: fetchData,
                    params: { pageSize: 20 }
                }}
                rowKey="classId"
                style={{width: '100%'}}
                locale={{
                    emptyText: <span className="ch-warning-text">No classes found</span>
                }}
            />
        </div>
    );
};

export default ClassesTable